/**
 * Role-Based Access Control Helpers for Support Hub
 */

export function normalizeRoles(roles?: (string | undefined | null)[] | string | null): string[] {
  if (!roles) return [];
  if (typeof roles === 'string') return [roles];
  if (!Array.isArray(roles)) return [];
  return roles.filter((r): r is string => typeof r === 'string' && r.trim().length > 0);
}

/**
 * Combines roles from explicit arguments or arrays.
 */
export function getEffectiveRoles(
  userRoles?: (string | undefined | null)[] | string | null,
  rbacRoles?: (string | undefined | null)[] | string | null
): string[] {
  const list: string[] = [];
  if (userRoles) {
    list.push(...normalizeRoles(userRoles));
  }
  if (rbacRoles) {
    list.push(...normalizeRoles(rbacRoles));
  }
  return Array.from(new Set(list));
}

/**
 * Super Admin or Support Admin has complete administrative access
 * to all 9 support tabs including Settings, Audit Log, and Reports.
 */
export function isSupportAdmin(roles?: (string | undefined | null)[] | string | null): boolean {
  const list = normalizeRoles(roles);
  const normalized = list.map((r) => String(r || '').toLowerCase().replace(/[_\s-]+/g, ''));
  return normalized.some((r) =>
    r === 'superadmin' ||
    r === 'supportadmin' ||
    r === 'admin' ||
    r === 'super' ||
    r === 'administrator' ||
    r === 'reportingmanager' ||
    r === 'manager' ||
    r === 'hradmin' ||
    r === 'platformadmin' ||
    r === 'supportmanager'
  );
}

/**
 * Support Agent has agent-level workspace access:
 * Dashboard, Tickets, Support Portal, Call Center, Live Chat, Knowledge Base.
 * (Settings, Audit Log, and Reports are excluded).
 */
export function isSupportAgent(roles?: (string | undefined | null)[] | string | null): boolean {
  const list = normalizeRoles(roles);
  const normalized = list.map((r) => String(r || '').toLowerCase().replace(/[_\s-]+/g, ''));
  return normalized.some((r) =>
    r === 'supportagent' ||
    r === 'agent' ||
    r === 'supportengineer' ||
    r === 'supportrep' ||
    r === 'technician' ||
    r === 'helpdesk'
  );
}

/**
 * External supplier agent (vendor access restricted strictly to external support view).
 * Pure external vendors only - any user holding internal, staff, or admin roles is NOT an external supplier.
 */
export function isExternalSupplier(roles?: (string | undefined | null)[] | string | null): boolean {
  const list = normalizeRoles(roles);
  if (list.length === 0) return false;
  if (isSupportAdmin(list)) return false;
  const normalized = list.map((r) => String(r || '').toLowerCase().replace(/[_\s-]+/g, ''));
  const hasInternalRole = normalized.some((r) =>
    r === 'superadmin' ||
    r === 'admin' ||
    r === 'supportadmin' ||
    r === 'supportmanager' ||
    r === 'platformadmin' ||
    r === 'reportingmanager' ||
    r === 'hradmin' ||
    r === 'employee' ||
    r === 'supportagent' ||
    r === 'agent'
  );
  if (hasInternalRole) return false;
  return normalized.some((r) => r === 'externalsupplier' || r === 'supplier');
}

/**
 * Pure Employee (without Support Admin or Support Agent privileges).
 * If user has Employee + Support Admin or Employee + Support Agent, returns false.
 * If roles are not yet loaded or empty, returns false so admins are never locked out by delay.
 */
export function isEmployeeOnlyForSupport(roles?: (string | undefined | null)[] | string | null): boolean {
  const list = normalizeRoles(roles);
  if (list.length === 0) return false;
  if (isSupportAdmin(list)) return false;
  if (isSupportAgent(list)) return false;
  return true;
}


